import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { analyticsRiderProfileFormOpened } from '@rideiq/api/services/Analytics';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';
import { useFlags } from '@rideiq/contexts/LDContext';
import { useToastStore } from '@rideiq/shared/stores/toastStore';

import { getAssociatedLocations, getRiderBySlug } from '../../../api/services/Riders';
import { trackFullStoryEvent } from '../../../vendor/features/FullStory/FullStory.service';

import { RiderCreationConfirmationModal } from './components/RiderCreationConfirmationModal';
import { mapLocationsToFormValues } from './utils/mapLocationsToFormValues';
import { mapRiderToFormValues } from './utils/mapRiderToFormValues';
import { RiderForm } from './RiderForm';
import { RiderFormProvider } from './RiderFormProvider';
import { RiderFormValues, SectionKey } from './types';

type RiderFormPageProps = {
  embedded?: boolean;
};

type InlineRideCreationConfirmation = 'created' | 'cancelled';

export const RiderFormPage = ({ embedded = false }: RiderFormPageProps) => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const { addToast } = useToastStore();
  const [defaults, setDefaults] = useState<Partial<RiderFormValues> | undefined>(() =>
    slug ? undefined : { firstName: searchParams.get('firstName') ?? '', lastName: searchParams.get('lastName') ?? '' }
  );
  const [currentRider, setCurrentRider] = useState<Pick<
    RiderFormType,
    'isSharedBillingRider' | 'sharedBillingRider' | 'slug'
  > | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(slug));
  const [inlineRideCreationConfirmation, setInlineRideCreationConfirmation] =
    useState<InlineRideCreationConfirmation | null>(null);
  const [createdRiderSlug, setCreatedRiderSlug] = useState<string | null>(null);

  const mode = slug ? 'edit' : 'create';
  const initialSection = mode === 'edit' ? (searchParams.get('section') as SectionKey | null) ?? undefined : undefined;
  const { enableSisImports } = useFlags();
  const { userSettings } = useAuthQuery();
  const sisEnabled = Boolean(enableSisImports && userSettings?.sisIntegration === 'Yes');
  const isInlineRideCreation = mode === 'create' && searchParams.get('origin') === 'ride_creation';
  const isRideSeries = searchParams.get('rideContext') === 'ride_series';
  const rideNoun = isRideSeries ? 'Ride Series' : 'Ride';
  const returnToRideLabel = isRideSeries ? 'Return to Series' : 'Return to Ride';
  const inlineRideCreationConfirmationBody = `You can now close this tab and return to creating a ${rideNoun}.`;

  useEffect(() => {
    const entryPoint = (() => {
      if (mode === 'create') {
        return 'add_rider_button' as const;
      }

      switch (searchParams.get('section')) {
        case 'contacts':
          return 'contact_card' as const;
        case 'locations':
          return 'rider_locations_card' as const;
        default:
          return 'edit_rider_button' as const;
      }
    })();

    // Rider creation opened inside a ride (embedded route or a new tab via ?origin=ride_creation)
    // isn't really "the Add Rider button" — give FullStory the accurate source without touching
    // the entry_point value Segment has always received for this call.
    const fullStorySource = mode === 'create' && (embedded || isInlineRideCreation) ? 'ride_creation' : entryPoint;

    if (mode === 'create') {
      trackFullStoryEvent('rider_profile_create_started', { source: fullStorySource });
    } else {
      trackFullStoryEvent('rider_profile_edit_started', { section_opened: fullStorySource });
    }

    if (embedded) {
      return;
    }

    analyticsRiderProfileFormOpened({ action: mode, entry_point: entryPoint, sis_enabled: sisEnabled });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = mode === 'create' ? 'Create Rider' : 'Edit Rider';
  const crumbs =
    mode === 'edit' && slug
      ? [{ title: 'Rider profile', pathname: `/riders/${slug}` }]
      : [{ title: 'All Riders', pathname: '/riders' }];

  useEffect(() => {
    if (!slug) {
      setIsLoading(false);

      return undefined;
    }
    let cancelled = false;

    setIsLoading(true);
    Promise.all([getRiderBySlug(slug), getAssociatedLocations(slug)]).then(([riderResponse, locationsResponse]) => {
      if (cancelled) {
        return;
      }

      const riderDefaults = riderResponse?.type === 'success' ? mapRiderToFormValues(riderResponse.data.passenger) : {};

      if (riderResponse?.type !== 'success') {
        addToast({ severity: 'error', message: 'Unable to load rider data. You can still fill in the form manually.' });
      }

      if (riderResponse?.type === 'success') {
        const passenger = riderResponse.data.passenger;

        setCurrentRider({
          slug,
          isSharedBillingRider: !!passenger.sharedBillingRider,
          sharedBillingRider: passenger.sharedBillingRider ?? null,
        });
      }

      const { locationDrafts, originalLocationIds } =
        locationsResponse?.type === 'success'
          ? mapLocationsToFormValues(locationsResponse.data.locations)
          : { locationDrafts: [], originalLocationIds: [] };

      setDefaults({ ...riderDefaults, locationDrafts, originalLocationIds });
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleClose = () => {
    if (isInlineRideCreation) {
      // Once a rider has actually been created, don't let a stale Cancel click (e.g. after the
      // "Rider created" modal was dismissed) downgrade it back to "Rider not created".
      setInlineRideCreationConfirmation((current) => current ?? 'cancelled');

      return;
    }

    if (embedded) {
      navigate('..');
    } else if (mode === 'edit' && slug) {
      navigate(`/riders/${slug}`);
    } else {
      navigate('/riders');
    }
  };

  const handleCreateConfirmation = (createdSlug: string) => {
    setCreatedRiderSlug(createdSlug);
    setInlineRideCreationConfirmation('created');
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <RiderFormProvider defaultValues={defaults}>
        <RiderForm
          title={title}
          crumbs={crumbs}
          onClose={handleClose}
          mode={mode}
          slug={slug}
          initialSection={initialSection}
          currentRider={currentRider}
          onCreateConfirmation={isInlineRideCreation ? handleCreateConfirmation : undefined}
        />
      </RiderFormProvider>
      {inlineRideCreationConfirmation && (
        <RiderCreationConfirmationModal
          title={inlineRideCreationConfirmation === 'created' ? 'Rider created' : 'Rider not created'}
          body={inlineRideCreationConfirmationBody}
          primaryActionLabel={returnToRideLabel}
          onPrimaryAction={() => window.close()}
          secondaryAction={
            inlineRideCreationConfirmation === 'created' && createdRiderSlug
              ? { label: 'Go to Rider Profile', onClick: () => navigate(`/riders/${createdRiderSlug}`) }
              : undefined
          }
        />
      )}
    </>
  );
};
